import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useEventLoader } from "../../../hooks/Events/useEventLoader";

export default function MemberPhotoGalleryTab() {
  const { id } = useParams<{ id: string }>();
  const event = useEventLoader(id);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number>(0);
  const [showThumbnails, setShowThumbnails] = useState<boolean>(true);
  const [thumbnailPage, setThumbnailPage] = useState<number>(0);
  const [photosPerPage, setPhotosPerPage] = useState<number>(8);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [pointerOverImageContent, setPointerOverImageContent] =
    useState<boolean>(false);
  const mainImageRef = useRef<HTMLImageElement | null>(null);

  /** `object-contain` paint area inside the img’s layout box; gutters = outside this rect */
  const updatePointerOverImageContent = useCallback(
    (clientX: number, clientY: number) => {
      const el = mainImageRef.current;
      if (!el?.naturalWidth || !el.naturalHeight) {
        setPointerOverImageContent(false);
        return;
      }
      const br = el.getBoundingClientRect();
      const nw = el.naturalWidth;
      const nh = el.naturalHeight;
      const scale = Math.min(br.width / nw, br.height / nh);
      const w = nw * scale;
      const h = nh * scale;
      const left = br.left + (br.width - w) / 2;
      const top = br.top + (br.height - h) / 2;
      const inside =
        clientX >= left &&
        clientX <= left + w &&
        clientY >= top &&
        clientY <= top + h;
      setPointerOverImageContent(inside);
    },
    []
  );

  const photos = useMemo(() => {
    if (!event) {
      return [];
    }

    const eventPhotos = event.photoGallery || [];
    return [...eventPhotos];
  }, [event]);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width < 640) {
        setIsMobile(true);
      } else if (width < 940) {
        setIsMobile(false);
        setPhotosPerPage(2);
      } else if (width < 1024) {
        setIsMobile(false);
        setPhotosPerPage(4);
      } else {
        setIsMobile(false);
        setPhotosPerPage(8);
      }
    };

    handleResize();

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (photos.length > 0) {
      const maxPage = Math.ceil(photos.length / photosPerPage) - 1;
      if (thumbnailPage > maxPage) {
        setThumbnailPage(maxPage >= 0 ? maxPage : 0);
      }
    }
  }, [photosPerPage, photos.length, thumbnailPage]);

  const totalThumbnailPages =
    photos.length > 0 ? Math.ceil(photos.length / photosPerPage) : 0;

  useEffect(() => {
    if (photos.length > 0) {
      const newPage = Math.floor(selectedImageIndex / photosPerPage);
      const maxPage = Math.ceil(photos.length / photosPerPage) - 1;
      if (newPage !== thumbnailPage && newPage >= 0 && newPage <= maxPage) {
        setThumbnailPage(newPage);
      }
    }
  }, [selectedImageIndex, photos.length]);

  if (!event) {
    return <div className="p-8 text-center text-gray-500">Event not found</div>;
  }

  if (photos.length === 0) {
    return (
      <div className="p-8">
        <div className="text-center py-12 text-gray-500">
          No photos available for this event.
        </div>
      </div>
    );
  }

  const mainImage = photos[selectedImageIndex] || photos[0];

  const thumbnailStartIndex = thumbnailPage * photosPerPage;
  const thumbnailEndIndex = thumbnailStartIndex + photosPerPage;
  const thumbnailImages = photos.slice(thumbnailStartIndex, thumbnailEndIndex);

  const mainNavBtnBase =
    "absolute top-1/2 z-10 -translate-y-1/2 flex h-12 w-12 items-center justify-center rounded-full border-2 transition-all duration-200";
  const mainNavOnImage = `${mainNavBtnBase} border-white/80 bg-black/35 shadow-lg hover:bg-black/50`;
  const mainNavInGutter = `${mainNavBtnBase} border-gray-500 bg-white text-gray-700 shadow-md hover:bg-gray-100`;

  return (
    <div className="p-8 bg-white min-h-screen">
      <div className="">
        <div className="flex items-center justify-end mb-6">
          <div className="flex gap-2">
            <button
              onClick={() => setShowThumbnails(!showThumbnails)}
              className={`p-2 rounded transition-colors ${
                showThumbnails
                  ? "bg-[#504f4f] text-white"
                  : "bg-gray-200 text-gray-600 hover:bg-gray-300"
              }`}
              aria-label={
                showThumbnails ? "Hide thumbnails" : "Show thumbnails"
              }
              title={showThumbnails ? "Hide thumbnails" : "Show thumbnails"}
            >
              {showThumbnails ? (
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                  />
                </svg>
              ) : (
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                  />
                </svg>
              )}
            </button>
            <button
              onClick={() => {
                const imageWindow = window.open("", "_blank");
                if (imageWindow) {
                  imageWindow.document.write(`
                    <!DOCTYPE html>
                    <html>
                      <head>
                        <title>Event Photo</title>
                        <style>
                          body {
                            margin: 0;
                            padding: 0;
                            display: flex;
                            justify-content: center;
                            align-items: center;
                            min-height: 100vh;
                            background-color: #000;
                          }
                          img {
                            max-width: 100%;
                            max-height: 100vh;
                            object-fit: contain;
                          }
                        </style>
                      </head>
                      <body>
                        <img src="${mainImage}" alt="Event Photo" />
                      </body>
                    </html>
                  `);
                  imageWindow.document.close();
                }
              }}
              className="p-2 rounded bg-gray-200 text-gray-600 hover:bg-gray-300 transition-colors"
              aria-label="Open photo in new tab"
              title="Open photo in new tab"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Main Image */}
        <div className="mb-6 flex justify-center">
          <div
            onMouseMove={(e) => updatePointerOverImageContent(e.clientX, e.clientY)}
            onMouseLeave={() => setPointerOverImageContent(false)}
            className="relative w-full max-w-5xl max-h-[550px] min-h-[200px] rounded-lg overflow-hidden bg-white"
          >
            <img
              ref={mainImageRef}
              src={mainImage}
              alt={`Event photo ${selectedImageIndex + 1}`}
              className="relative z-0 w-full max-h-[550px] object-contain"
              onLoad={() => setPointerOverImageContent(false)}
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />

            {photos.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={() =>
                    setSelectedImageIndex(
                      selectedImageIndex > 0
                        ? selectedImageIndex - 1
                        : photos.length - 1
                    )
                  }
                  className={`left-4 ${pointerOverImageContent ? mainNavOnImage : mainNavInGutter}`}
                  aria-label="Previous photo"
                >
                  <svg
                    className={
                      pointerOverImageContent
                        ? "h-6 w-6 text-white"
                        : "h-6 w-6 text-gray-700"
                    }
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2.5}
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                </button>

                <button
                  type="button"
                  onClick={() =>
                    setSelectedImageIndex(
                      selectedImageIndex < photos.length - 1
                        ? selectedImageIndex + 1
                        : 0
                    )
                  }
                  className={`right-4 ${pointerOverImageContent ? mainNavOnImage : mainNavInGutter}`}
                  aria-label="Next photo"
                >
                  <svg
                    className={
                      pointerOverImageContent
                        ? "h-6 w-6 text-white"
                        : "h-6 w-6 text-gray-700"
                    }
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2.5}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>
              </>
            )}
          </div>
        </div>

        {/* Thumbnail Images */}
        {showThumbnails && !isMobile && (
          <div className="mb-6">
            <div className="relative flex items-center justify-center gap-4">
              {/* Left Arrow - Show when more photos than per page */}
              {photos.length > photosPerPage && (
                <button
                  onClick={() => {
                    if (thumbnailPage > 0) {
                      setThumbnailPage(thumbnailPage - 1);
                    } else {
                      setThumbnailPage(totalThumbnailPages - 1);
                    }
                  }}
                  className="w-10 h-10 rounded-full border-2 border-gray-400 bg-white hover:bg-gray-100 transition-all flex items-center justify-center shadow-lg flex-shrink-0"
                  aria-label="Previous thumbnails"
                >
                  <svg
                    className="w-5 h-5 text-gray-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2.5}
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                </button>
              )}

              {/* Thumbnails */}
              <div className="flex justify-center gap-4 flex-wrap">
                {thumbnailImages.map((photo, index) => {
                  const photoIndex = thumbnailStartIndex + index;
                  return (
                    <button
                      key={photoIndex}
                      onClick={() => {
                        setSelectedImageIndex(photoIndex);
                      }}
                      className={`relative w-32 h-32 rounded-lg overflow-hidden border-2 transition-all ${
                        selectedImageIndex === photoIndex
                          ? "border-primary scale-105 shadow-lg"
                          : "border-gray-300 hover:border-gray-400"
                      }`}
                    >
                      <img
                        src={photo}
                        alt={`Thumbnail ${photoIndex + 1}`}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    </button>
                  );
                })}
              </div>

              {/* Right Arrow - Show when more photos than per page */}
              {photos.length > photosPerPage && (
                <button
                  onClick={() => {
                    if (thumbnailPage < totalThumbnailPages - 1) {
                      setThumbnailPage(thumbnailPage + 1);
                    } else {
                      setThumbnailPage(0);
                    }
                  }}
                  className="w-10 h-10 rounded-full border-2 border-gray-400 bg-white hover:bg-gray-100 transition-all flex items-center justify-center shadow-lg flex-shrink-0"
                  aria-label="Next thumbnails"
                >
                  <svg
                    className="w-5 h-5 text-gray-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2.5}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
